import Chip from "@mui/material/Chip";
import { alpha } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";

/** Matches the runtime notification contract's severity scale exactly. */
export type SeverityAccentLevel = "info" | "warning" | "medium" | "high" | "critical";

interface SeverityAccentProps {
  level: SeverityAccentLevel;
  label: string;
}

/**
 * Returns a distinct accent color per severity level, sourced from the theme's
 * `severity` palette. Unlike StatusBadge's semantic palette (which groups high/medium
 * and low/info), every level here renders with its own hue so notification stripes and
 * chips stay visually distinguishable at a glance.
 */
export function getSeverityAccentColor(theme: Theme, level: SeverityAccentLevel): string {
  switch (level) {
    case "critical":
      return theme.palette.severity.critical.main;
    case "high":
      return theme.palette.severity.high.main;
    case "medium":
      return theme.palette.severity.medium.main;
    case "warning":
      return theme.palette.severity.warning.main;
    case "info":
      return theme.palette.severity.info.main;
    default:
      return theme.palette.text.secondary;
  }
}

/**
 * Returns the legible-text variant for a severity level: brighter than
 * `getSeverityAccentColor`'s stripe/border tone so labels stay above 4.5:1 contrast
 * when rendered on top of their own alpha-tinted chip background.
 */
function getSeverityAccentTextColor(theme: Theme, level: SeverityAccentLevel): string {
  switch (level) {
    case "critical":
      return theme.palette.severity.critical.light;
    case "high":
      return theme.palette.severity.high.light;
    case "medium":
      return theme.palette.severity.medium.light;
    case "warning":
      return theme.palette.severity.warning.light;
    case "info":
      return theme.palette.severity.info.light;
    default:
      return theme.palette.text.secondary;
  }
}

/**
 * Compact severity chip with a distinct color per level (info/low/medium/high/critical).
 * Color is always paired with a text label so color is never the sole signal.
 */
export function SeverityAccent({ level, label }: SeverityAccentProps) {
  return (
    <Chip
      label={label}
      size="small"
      sx={(theme) => {
        const color = getSeverityAccentColor(theme, level);
        const textColor = getSeverityAccentTextColor(theme, level);
        return {
          height: 20,
          fontSize: "0.625rem",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          borderRadius: "4px",
          backgroundColor: alpha(color, 0.15),
          color: textColor,
          border: `1px solid ${alpha(color, 0.35)}`,
          "& .MuiChip-label": { px: 1 },
        };
      }}
    />
  );
}
